import core from '@gin-betting/core';
import { createSelector } from 'reselect';

import { LIVE_SPORTEVENT_STATUSES, SportEventMetaKey } from 'app-constants';
import type { State } from 'types';
import type { StreamState } from './types';
import { isStreamUrlValid } from './utils';

const {
    entities: {
        selectors: { selectMatchIdFromProps, selectMatches, selectStreams },
    },
    utils: { getStreamWithCurrentLocale, joinIdPath },
    viewport: {
        selectors: { selectIsCompact },
    },
} = core;

export const selectStream = (state: State): StreamState => state.stream;

export const selectMatchIdFullScreen = createSelector(
    selectStream,
    stream => stream.matchIdFullScreen
);

export const selectViewerMode = createSelector(
    selectStream,
    stream => stream.viewerMode
);

export const selectIsFullScreen = createSelector(
    selectMatchIdFullScreen,
    matchId => matchId !== null
);

export const selectPinnedStreamMatchId = createSelector(
    selectStream,
    stream => stream.pinnedStreamMatchId
);

export const selectMatchIdsWithOpenedStream = createSelector(
    selectStream,
    stream => stream.matchIds
);

export const selectIsMatchIdWithOpenedStream = createSelector(
    selectMatchIdFromProps,
    selectMatchIdsWithOpenedStream,
    (matchId, matchIds) => matchIds.has(matchId)
);

export const selectMatchIdsFromProps = (
    _: State,
    { matchIds }: { matchIds: string[] }
) => matchIds;

// TODO: DRY with next
export const selectPinnedStreamInfo = createSelector(
    selectPinnedStreamMatchId,
    selectMatches,
    selectStreams,
    selectIsCompact,
    (
        pinnedStreamMatchId,
        matches,
        streams,
        isCompact
    ): {
        pinnedStreamUrl: null | string;
        pinnedStreamTitle: null | string;
        pinnedStreamMatchId: null | string;
    } => {
        const match = matches.get(pinnedStreamMatchId || '');

        if (!match) {
            return {
                pinnedStreamUrl: null,
                pinnedStreamTitle: null,
                pinnedStreamMatchId: null,
            };
        }

        const streamUrl = getStreamWithCurrentLocale({
            allStreams: streams,
            streamIds: match.streams,
            isCompact,
        });

        return {
            pinnedStreamUrl: isStreamUrlValid(streamUrl) ? streamUrl : null,
            pinnedStreamTitle: match.title,
            pinnedStreamMatchId: match.id,
        };
    }
);

// TODO: move to core
export const selectLiveMatchesByIds = createSelector(
    selectMatchIdsFromProps,
    selectMatches,
    selectStreams,
    selectIsCompact,
    (matchIds, allMatches, allStreams, isCompact) => {
        return matchIds.filter(matchId => {
            const match = allMatches.get(matchId);

            if (!match) return false;

            const { status, streams: streamIds, meta } = match;

            const streamUrl = getStreamWithCurrentLocale({
                allStreams,
                streamIds,
                isCompact,
            });

            const isStreamValid =
                LIVE_SPORTEVENT_STATUSES.includes(status) &&
                isStreamUrlValid(streamUrl);

            const doesBetradarExist = !!meta?.includes(
                joinIdPath(matchId, SportEventMetaKey.BetradarId)
            );

            return isStreamValid || doesBetradarExist;
        });
    }
);

export const selectLastPlayedStreamMatchId = createSelector(
    selectStream,
    stream => stream.get('lastPlayedStreamMatchId')
);

export const selectLoadScriptStatus = createSelector(
    selectStream,
    matchTracker => matchTracker.get('matchTrackerStatus')
);
